/**
 * Syndication service - API calls for Phase 7 Investor Syndication Layer
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  SyndicationRound,
  SyndicationListFilters,
  SyndicationListResponse,
  CreateSyndicationRoundRequest,
  UpdateSyndicationRoundRequest,
  SyndicationCommitment,
  CreateCommitmentRequest,
  UpdateCommitmentRequest,
  WireUpdateRequest,
  ApplyAllocationRequest,
  InitiateSpvRequest,
  SpvStatusResponse,
} from '../types/syndication';

function errMsg(e: any, fallback: string): string {
  return e?.response?.data?.detail || e?.message || fallback;
}

// -------- Rounds --------

export async function fetchRounds(
  filters?: SyndicationListFilters
): Promise<ApiResponse<SyndicationListResponse>> {
  try {
    const response = await api.get('/syndication/rounds', { params: filters });
    const payload = response.data;
    if (Array.isArray(payload)) {
      return {
        data: {
          rounds: payload,
          total: payload.length,
          page: 1,
          size: payload.length,
          pages: 1,
        },
      };
    }
    return {
      data: {
        rounds: payload?.rounds || payload?.items || [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        size: payload?.size ?? 50,
        pages: payload?.pages ?? 1,
      },
    };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load syndication rounds') };
  }
}

export async function fetchRound(
  id: number
): Promise<ApiResponse<SyndicationRound>> {
  try {
    const response = await api.get(`/syndication/rounds/${id}`);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load syndication round') };
  }
}

export async function createRound(
  req: CreateSyndicationRoundRequest
): Promise<ApiResponse<SyndicationRound>> {
  try {
    const response = await api.post('/syndication/rounds', req);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to create syndication round') };
  }
}

export async function updateRound(
  id: number,
  req: UpdateSyndicationRoundRequest
): Promise<ApiResponse<SyndicationRound>> {
  try {
    const response = await api.patch(`/syndication/rounds/${id}`, req);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to update syndication round') };
  }
}

export async function deleteRound(
  id: number
): Promise<ApiResponse<{ success: true }>> {
  try {
    await api.delete(`/syndication/rounds/${id}`);
    return { data: { success: true } };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to delete syndication round') };
  }
}

// -------- Commitments --------

export async function createCommitment(
  roundId: number,
  req: CreateCommitmentRequest
): Promise<ApiResponse<SyndicationCommitment>> {
  try {
    const response = await api.post(
      `/syndication/rounds/${roundId}/commitments`,
      req
    );
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to create commitment') };
  }
}

export async function updateCommitment(
  id: number,
  req: UpdateCommitmentRequest
): Promise<ApiResponse<SyndicationCommitment>> {
  try {
    const response = await api.patch(`/syndication/commitments/${id}`, req);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to update commitment') };
  }
}

export async function deleteCommitment(
  id: number
): Promise<ApiResponse<{ success: true }>> {
  try {
    await api.delete(`/syndication/commitments/${id}`);
    return { data: { success: true } };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to delete commitment') };
  }
}

// -------- Wires --------

export async function updateWire(
  commitmentId: number,
  req: WireUpdateRequest
): Promise<ApiResponse<SyndicationCommitment>> {
  try {
    const response = await api.post(
      `/syndication/commitments/${commitmentId}/wire`,
      req
    );
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to update wire status') };
  }
}

// -------- Allocation --------

export async function applyAllocation(
  roundId: number,
  req: ApplyAllocationRequest
): Promise<ApiResponse<SyndicationRound>> {
  try {
    const response = await api.post(
      `/syndication/rounds/${roundId}/allocate`,
      req
    );
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to apply allocation') };
  }
}

// -------- SPV --------

export async function initiateSpv(
  roundId: number,
  req: InitiateSpvRequest
): Promise<ApiResponse<SpvStatusResponse>> {
  try {
    const response = await api.post(
      `/syndication/rounds/${roundId}/spv/initiate`,
      req
    );
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to initiate SPV') };
  }
}

export async function fetchSpvStatus(
  roundId: number
): Promise<ApiResponse<SpvStatusResponse>> {
  try {
    const response = await api.get(`/syndication/rounds/${roundId}/spv/status`);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to fetch SPV status') };
  }
}

// -------- Close --------

export async function closeRound(
  roundId: number
): Promise<ApiResponse<SyndicationRound>> {
  try {
    const response = await api.post(`/syndication/rounds/${roundId}/close`);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to close round') };
  }
}

const syndicationService = {
  fetchRounds,
  fetchRound,
  createRound,
  updateRound,
  deleteRound,
  createCommitment,
  updateCommitment,
  deleteCommitment,
  updateWire,
  applyAllocation,
  initiateSpv,
  fetchSpvStatus,
  closeRound,
};

export default syndicationService;
